import React, { Fragment, useMemo } from 'react'
import { Row, Col } from 'react-flexbox-grid'
import { withApollo } from '@apollo/client/react/hoc'
import { withRouter } from 'react-router'
import { compose } from 'recompose'
import { useQuery } from 'components/graphql'

import { Form, Text, TextArea, Checkbox, Select } from 'components/forms'
import PriceForm from 'components/forms/fragments/PriceForm'
import { ContentBlock } from 'components/ContentBlock'
import Button from 'components/Button'
import Divider from 'components/Divider'
import { notEmpty } from 'helpers/validators'
import listCategories from 'sections/category/gql/listCategories'
import getAppConfig from 'gql/query/appConfig'
import { loadAppConstants, listSalesGroups, listShops } from 'gql/query'
import { useTranslation } from 'react-i18next'

const Product = ({ appData, data, formData = {}, onChangeMultiEdit, apiRef, newRecord, ConfirmButton, ConfirmModal, mutations: { updateItem, createItem }, history, client, multiEdit }) => {
	const [t, i18n] = useTranslation()

	const getDisabledLevels = (vals, isCafe) => {
		return Array.isArray(vals) ? vals.reduce((outArr, item, i) => {
			if(item > 0) {
				outArr.push(`price${isCafe ? 'InCafe' : ''}[${i}]`)
			}
			return outArr
		}, [])
			: []
	}
	const handleSubmit = async (values) => newRecord ? createItem(values) : updateItem(values)
	const handleServerError = (formApi, field, error) => {
		formApi.setError(field, field === 'code' ? t('product_section.errors.duplicate_field_code') : error.message)
	}

	const { data: { getAppConfig: priceLevels = {} } } = useQuery(getAppConfig.usedFAPriceLevels, {
		fetchPolicy: 'cache-first'
	})
	const disabledLevels = [
		...getDisabledLevels(priceLevels?.priceLevelsLinkFA),
		...getDisabledLevels(priceLevels?.priceLevelsCafeLinkFA, true)
	]

	const isLinkFA = data && data.FA_ProductId
	const faTitle = useMemo(() => t('product_section.strings.product_from_FA'), [isLinkFA])


	return(
		<Form
			apiRef={apiRef}
			onChange={(e) => multiEdit?.isActive && onChangeMultiEdit(e, formData)}
			initialValues={formData}
			onSubmit={handleSubmit}
			smartSubmit={(!newRecord && !multiEdit?.isActive)}
			className='flex-col-container'
			updateByInitial
			onServerError={handleServerError}
		>
			{({ formState, formApi }) => (
				<Fragment>
					<ContentBlock>
						<Row>
							<Col xs>
								<div>
									<Text
										field='code'
										label={t('product_section.fields.number')}
										placeholder='Nummer eintragen'
										{...notEmpty()}
										disabled={isLinkFA || multiEdit.isActive}
										{...isLinkFA && { title: faTitle }}
										validateOnChange
										inline />
									<Checkbox
										inline={true}
										field='isActive'
										label={t('product_section.fields.active')}
										disabled={isLinkFA}
										title={t('product_section.strings.product_from_FA')} />
									{ConfirmModal()}
								</div>
								<Text
									label={t('product_section.fields.name')}
									field='name'
									placeholder='Bezeichnung eintragen'
									{...notEmpty()}
									disabled={isLinkFA || multiEdit.isActive}
									{...isLinkFA && { title: faTitle }}
									validateOnChange />
								<TextArea
									label={t('product_section.fields.alt_name')}
									field='altName'
									placeholder=''
								/>
							</Col>
							<Col xs>
								<Select
									label={t('product_section.fields.category')}
									field='categoryId'
									valueField='id'
									labelField='name'
									withVirtualBody
									isSearchable
									remoteOptions={{
										query: listCategories,
										sort: [{ field: 'position', order: 'ASC' }],
										modifyOptions: pos => pos
											.map(item => ({ id: item?.id, name: [`${item?.position} | ${item?.name}`] }))
									}}
									simpleValue={true}
									disabled={isLinkFA}
									{...isLinkFA && { title: faTitle }}
								/>
								<Select
									remoteOptions={{
										query: listSalesGroups,
										sort: [{ field: 'acountNum', order: 'ASC' }],
										modifyOptions: num => num
											.map(item => ({ id: item?.id, name: [`${item?.acountNum} | ${item?.name}`] }))
									}}
									label={t('product_section.fields.sales_group')}
									field='salesGroupId'
									{...newRecord && { ...notEmpty() }}
									validateOnChange
									valueField='id'
									labelField='name'
									disabled={isLinkFA}
									{...isLinkFA && { title: faTitle }}
								/>
								<Select
									label={t('product_section.fields.sales_unit')}
									field='unitSellCode'
									disabled={isLinkFA}
									{...isLinkFA && { title: faTitle }}
									remoteOptions={{ query: loadAppConstants, variables: { type: 34, language: i18n.language } }}
								/>
								{appData.isShopFieldActive &&
									<Select
										label={t('common.fields.shop_affiliation')}
										field='shopId'
										valueField='id'
										labelField='name'
										validate={val => !val && appData.shopAccess ? t('validators.required') : null}
										remoteOptions={{
											query: listShops,
											modifyOptions: (options => {
												return options
													.map(opt => opt.codePrefix ? ({ ...opt, name: `${opt.name} - "${opt.codePrefix}"` }) : opt)
											})
										}}
										isClearable={!appData.shopAccess}
									/>
								}
							</Col>
							<Col xs>
								<p className='form-group-label'>{t('product_section.strings.discount')}</p>
								<Checkbox field='isNetto' label={t('product_section.fields.customer_net_product')} />
								<Checkbox field='isNettoForUser' label={t('product_section.fields.empoloyee_net_product')} />
								<Checkbox field='isEmployeeArticle' label={t('product_section.fields.empoloyee_meals')} />
							</Col>
						</Row>
					</ContentBlock>
					<ContentBlock label={t('product_section.strings.prices')} related>
						{isLinkFA ?
							<PriceForm disabledFields={disabledLevels} enableNegative /> :
							<PriceForm enableNegative />
						}
						{ConfirmButton(formState)}
						{newRecord &&
							<Fragment>
								<Divider height={20}/>
								<Col type='row'>
									<Button type='submit' status='primary' text={t('common.buttons.create')} />
								</Col>
							</Fragment>
						}
					</ContentBlock>
				</Fragment>
			)}
		</Form>
	)
}

export default compose(withRouter, withApollo)(Product)
